import { Component, Inject } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DataService } from '../data/data.service';
import { CashBoxCloseModalComponent } from './cash-box-close-modal.component';
import { AppConfigService } from '../app-config/app-config.service';
import { APP_SETTINGS } from '../data/settings-data.provider';
import { Settings } from '../data/models';

@Component({
    selector: 'app-cash-register-actions',
    styles: [`
    :host {
      display: block;
    }
    .qrcode-btn {
      min-width: 180px;
      justify-content: center;
    }
    .qrcode-wrapper,
    .qrcode-wrapper-inner {
      position: relative;
      &::after,
      &::before {
        content: '';
        display: block;
        position: absolute;
        width: 50px;
        height: 50px;
        z-index: 1;
      }
    }
    .qrcode-wrapper {
      &::after,
      &::before {
        top: 5px;
        border-top: 1px solid #ccc;
      }
      &::after {
        left: 5px;
        border-top-left-radius: 10px;
        border-left: 1px solid #ccc;
      }
      &::before {
        right: 5px;
        border-top-right-radius: 10px;
        border-right: 1px solid #ccc;
      }
    }
    .qrcode-wrapper-inner {
      width: 255px;
      height: 260px;
      &::after,
      &::before {
        bottom: 10px;
        border-bottom: 1px solid #ccc;
      }
      &::after {
        left: 5px;
        border-bottom-left-radius: 10px;
        border-left: 1px solid #ccc;
      }
      &::before {
        right: 5px;
        border-bottom-right-radius: 10px;
        border-right: 1px solid #ccc;
      }
    }
    .qrcode-desc {
      min-height: 40px;
    }
    .qrcode-scanner-btn {
      width: 368px;
      justify-content: center;
    }
    .qrcode-wrapper--empty {
      ::before,
      ::after,
      &::after,
      &::before {
        display: none;
      }
    }
  `],
    template: `
    <header class="modal-header">
      <h5 class="modal-title" id="exampleModalLabel" i18n>{{ headerCaption }}</h5>
      <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" (click)="modalService.dismissAll()"></button>
    </header>

    <div class="modal-body d-flex flex-column">
      <ng-container *ngIf="showQRContent">
        <div class="d-flex flex-column align-items-center p-4 qrcode">
          <h6 class="mb-3">{{getQRTitle()}}</h6>
          <section
            class="qrcode-wrapper"
            [ngClass]="{'qrcode-wrapper--empty': !qrCodeData}"
          >
            <section class="qrcode-wrapper-inner">
              <qrcode
                *ngIf="!!qrCodeData"
                [qrdata]="qrCodeData"
                [width]="256"
                [errorCorrectionLevel]="'M'"
                [elementType]="'canvas'"
                class="mb-3">
              </qrcode>
            </section>
          </section>
          <p class="text-muted small text-center mb-3 qrcode-desc">
            {{getQRDescription()}}
          </p>
          <div class="d-flex gap-2 flex-wrap justify-content-center mb-2">
            <button
              class="btn btn-outline-info d-flex align-items-center gap-2 qrcode-btn"
              [ngClass]="{
                'btn-info': qrCodeType === 'play-store',
                'btn-outline-info': qrCodeType !== 'play-store'
              }"
              (click)="showPlayStoreQR()"
              i18n
            >
              <span class="material-icons">android</span>
              Google Play Store
            </button>
            <button
              class="btn d-flex align-items-center gap-2 qrcode-btn"
              [ngClass]="{
                'btn-info': qrCodeType === 'app-store',
                'btn-outline-info': qrCodeType !== 'app-store'
              }"
              (click)="showAppStoreQR()"
              i18n
            >
              <span class="material-icons">apple</span>
              Apple App Store
            </button>
          </div>
          <div class="d-flex gap-2 flex-wrap justify-content-center mb-2">
            <button
              class="btn d-flex align-items-center gap-2 qrcode-scanner-btn"
              (click)="openQR()"
              [ngClass]="{
                'btn-info': qrCodeType === 'scanner-connect',
                'btn-outline-info': qrCodeType !== 'scanner-connect'
              }"
              i18n
            >
              <span class="material-icons">qr_code_scanner</span>
              Kopplung starten
            </button>
          </div>
        </div>
      </ng-container>

      <ng-container *ngIf="!showQRContent">
        <div *ngIf="!dataService.cashBoxClosed" class="d-flex justify-content-between align-items-center py-1 m-2">
          <span class="fs-5" i18n>
            Aktueller Kassenstand
          </span>
          <span class="fs-5 text-info">
            {{dataService.cashBox.cashBalance | currency}}
          </span>
        </div>
        <button class="btn btn-info btn-lg m-2" i18n (click)="refreshApp()">
          Kassensystem aktualisieren
        </button>
        <button class="btn btn-info btn-lg m-2" i18n (click)="openCashDrawer()">
          Schublade öffnen
        </button>
        <!-- TODO: Add remote scanner connection button after new mobile app release -->
        <!-- <button class="btn btn-info btn-lg m-2" i18n (click)="openQRArea()">
          Remote Scanner verbinden
        </button> -->
        <button class="btn btn-primary btn-lg m-2" i18n (click)="closeCashbox()">
          Kassenabschluss
        </button>
      </ng-container>
    </div>
  `,
    standalone: false
})
export class CashRegisterActionsComponent {
  constructor(
    public readonly modalService: NgbModal,
    public readonly dataService: DataService,
    private readonly appConfigService: AppConfigService,
    @Inject(APP_SETTINGS) private readonly settings: Settings
  ) {
  }

  showQRContent = false;
  qrCodeData = '';
  qrCodeType: 'scanner-connect' | 'play-store' | 'app-store' | '' = '';

  get headerCaption() {
    if (this.showQRContent) {
      return 'Remote Scanner';
    }

    return 'Kasse';
  }

  refreshApp() {
    location.reload();
  }

  closeCashbox() {
    this.modalService.dismissAll();
    this.modalService.open(CashBoxCloseModalComponent, { size: 'lg' });
  }

  openQR() {
    const appConfig = this.appConfigService.getConfig();

    if (!appConfig) {
      console.error('App configuration not available');
      return;
    }

    const apiKey = appConfig.apiKey;
    const cashBoxUuid = this.settings.posUuid;

    // Create a JSON object with the scanner connection data
    // When scanned, the app will receive this object with:
    // - apiKey: Shop API key for authentication
    // - cashBoxUuid: Cash box UUID for connecting to the specific POS
    const scannerData = {
      apiKey: apiKey,
      cashBoxUuid: cashBoxUuid,
      shopSubDomain: this.settings.subdomain,
    };

    // Convert to JSON string for QR code
    this.qrCodeData = JSON.stringify(scannerData);
    this.qrCodeType = 'scanner-connect';
    this.showQRContent = true;
  }

  openQRArea() {
    this.showQRContent = true;
  }

  showPlayStoreQR() {
    // QR code that leads to the app in Google Play Store
    // TODO: Replace with actual Google Play URL when available
    this.qrCodeData = 'https://play.google.com/store/apps/details?id=de.versacommerce.pos';
    this.qrCodeType = 'play-store';
  }

  showAppStoreQR() {
    // QR code that leads to the app in Apple App Store
    // TODO: Replace with actual App Store URL when available
    this.qrCodeData = 'https://apps.apple.com/app/versacommerce-pos/id123456789';
    this.qrCodeType = 'app-store';
  }

  getQRTitle(): string {
    switch (this.qrCodeType) {
      case 'play-store':
        return 'QR-Code scannen, um die App aus dem Google Play Store zu installieren.';
      case 'app-store':
        return 'QR-Code scannen, um die App aus dem Apple App Store zu installieren.';
      default:
        return 'QR-Code scannen, um ein mobiles Endgerät als Barcode-Scanner zu verwenden.';
    }
  }

  getQRDescription(): string {
    switch (this.qrCodeType) {
      case 'play-store':
        return 'Dieser QR-Code führt zur Remote Barcode Scanner App im Google Play Store.';
      case 'app-store':
        return 'Dieser QR-Code führt zur Remote Barcode Scanner App im Apple App Store.';
      default:
        return 'Hinweis: Die App Remote Barcode Scanner muss auf dem Endgerät installiert sein.';
    }
  }

  async openCashDrawer() {
    await this.dataService.openCashDrawer();
    this.modalService.dismissAll();
  }
}
